/**
 * SectionEditorPane — editor central del Designer (B.6.d.2).
 *
 * Cuando hay una sección seleccionada, muestra:
 *   - Header con title + BlockTypeBadge + acciones (quitar sección).
 *   - Tabs ES/EN para editar el lado i18n.
 *   - Campos: title visible, section_id (read-only post-create — hoy
 *     siempre read-only para simplificar; en B.6.d.2.b se permite
 *     edición solo cuando la sección está recién creada y no se ha
 *     guardado nunca).
 *   - Selector de block type (dropdown solo con `available: true`).
 *   - Toggles `required` y `repeatable`.
 *   - Validation rules (vista MVP: solo min/max length/items según tipo).
 *   - Bloque "Asistencia IA" placeholder — el wiring real es B.6.d.4.
 *
 * Cuando no hay sección seleccionada, muestra empty state.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Code2, Plus, Trash2 } from 'lucide-react'

import { RepeatableDiscriminatorDialog } from './RepeatableDiscriminatorDialog'

import type {
  ArtifactStructuralSection,
  BlockType,
} from '../../../api/templates'
import { useCrossRefCandidates } from '../../../hooks/useCrossRefCandidates'
import { AiInstructionsBlock } from './AiInstructionsBlock'
import {
  ContextSectionsBlock,
  type AvailableIntraRef,
} from './ContextSectionsBlock'
import { ValidationRulesBlock } from './ValidationRulesBlock'
import { AddSectionPickerDialog } from './AddSectionPickerDialog'
import { BlockTypeBadge } from './BlockTypeBadge'
import { TableConfigBlock } from './TableConfigBlock'

/** Parser para campos numéricos del editor (min_items, max_items,
 * min_length, max_length). Devuelve el número solo si es finito y >= 1.
 * Para 0, negativos, NaN o vacío devuelve `undefined` (interpretado
 * como "sin restricción" por los reducers).
 *
 * Razón: el backend Pydantic tiene `ge=1` en estos campos, así que
 * persistir 0 produce 400 al autosave. Mejor que el editor lo trate
 * como undefined antes de salir.
 */
function parsePositiveInt(raw: string): number | undefined {
  const trimmed = raw.trim()
  if (!trimmed) return undefined
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 1 || !Number.isInteger(n)) return undefined
  return n
}

// Valores que acepta el backend para `variant` (OT-0006).
const CALLOUT_VARIANTS = [
  'note',
  'info',
  'tip',
  'warning',
  'critical',
  'success',
  'reference',
  'instruction',
] as const

/** Lenguajes válidos para `code_block`. Replicado del runtime
 * SectionEditor (official-viewer) — cuando el set crezca, extraer a un
 * archivo compartido. La cadena vacía representa "sin lenguaje". */
const CODE_LANGUAGES = [
  '',
  'typescript',
  'javascript',
  'python',
  'sql',
  'json',
  'yaml',
  'bash',
  'go',
  'rust',
  'java',
  'csharp',
  'html',
  'css',
] as const
import { Dialog } from '../../Dialog'
import {
  backwardRefCandidates,
  findSectionI18n,
  findSectionStructural,
  isContainerType,
  walkTree,
  type DesignerAction,
  type DesignerState,
} from './state'
import { SUPPORTED_LOCALES, type SupportedLocale } from '../../../i18n/config'

interface SectionEditorPaneProps {
  state: DesignerState
  dispatch: React.Dispatch<DesignerAction>
  readOnly: boolean
  /** ADR-0002 Fase 10.f.1 — template_key del template en edición, para
   * excluirlo del dropdown de cross-refs (no auto-referencia). Opcional:
   * si no se pasa, el dropdown muestra TODO el catálogo. */
  templateKey?: string
}

export function SectionEditorPane({
  state,
  dispatch,
  readOnly,
  templateKey,
}: SectionEditorPaneProps) {
  const { t } = useTranslation('templates')

  if (!state.selectedSectionId) {
    return (
      <div className="h-full flex items-center justify-center p-12">
        <div className="text-center max-w-md">
          <h3 className="font-display text-lg text-ink mb-2">
            {t('designer.editor.no_selection_title')}
          </h3>
          <p className="font-body text-sm text-ink-muted">
            {t('designer.editor.no_selection_body')}
          </p>
        </div>
      </div>
    )
  }

  const structural = findSectionStructural(
    state.definition,
    state.selectedSectionId,
  )
  if (!structural) return null

  return (
    <SectionEditorBody
      state={state}
      dispatch={dispatch}
      readOnly={readOnly}
      structural={structural}
      {...(templateKey !== undefined ? { templateKey } : {})}
    />
  )
}

// ─── Body ──────────────────────────────────────────────────────────────────
function SectionEditorBody({
  state,
  dispatch,
  readOnly,
  structural,
  templateKey,
}: {
  state: DesignerState
  dispatch: React.Dispatch<DesignerAction>
  readOnly: boolean
  structural: ArtifactStructuralSection
  templateKey?: string
}) {
  const { t } = useTranslation('templates')
  const [removeOpen, setRemoveOpen] = useState(false)
  // Doc 06 §3.3.1 — dialog que aparece al marcar repeatable en un
  // container que aún no tiene un primer hijo `plain_text required`.
  // El reason indica qué copy mostrar y qué hace la opción "auto":
  //   - 'empty'       → no hay hijos, crear discriminador nuevo.
  //   - 'wrong_first' → hay hijos pero el primero no es plain_text
  //                     required, promover o insertar uno nuevo.
  const [repeatableDialog, setRepeatableDialog] = useState<{
    open: boolean
    reason: 'empty' | 'wrong_first'
  } | null>(null)
  // ADR-0002 Fase 10.f.1 — cross-ref candidates desde el catálogo del
  // tenant. El hook cachea por templateKey (60s).
  const { data: crossRefCandidates = [] } = useCrossRefCandidates(templateKey)

  const i18nSection = findSectionI18n(
    state.definition,
    structural.section_id,
    state.activeLocale,
  )
  const title = i18nSection?.title ?? ''

  const blockType = structural.type as BlockType
  const isContainer = isContainerType(structural.type)

  // Para el hint de repetible: ¿cuál es el primer hijo de texto que va a
  // servir como rótulo de cada instancia en runtime? Si lo hay, mostramos
  // su título actual; si no, fallback a "<group> #N".
  const firstTextChild = structural.children?.find(
    (c) => c.type === 'plain_text' || c.type === 'rich_text',
  )
  const firstTextChildLabel = firstTextChild
    ? findSectionI18n(
        state.definition,
        firstTextChild.section_id,
        state.activeLocale,
      )?.title ?? firstTextChild.section_id
    : null

  return (
    <div className="h-full overflow-y-auto p-6">
      {/* Header del nodo */}
      <header className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <BlockTypeBadge type={blockType} size="sm" />
          <h2 className="font-display text-xl font-semibold text-ink truncate">
            {title || structural.section_id}
          </h2>
        </div>
        {!readOnly ? (
          <button
            type="button"
            onClick={() => setRemoveOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-critical px-2 py-1 rounded-md hover:bg-critical-soft transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t('designer.editor.remove_section')}
          </button>
        ) : null}
      </header>

      {/* Tabs de locale */}
      <LocaleTabs
        active={state.activeLocale}
        availableLocales={Object.keys(state.definition.i18n) as SupportedLocale[]}
        onChange={(loc) => dispatch({ type: 'SET_LOCALE', locale: loc })}
      />

      {/* Campos i18n */}
      <div className="mt-4 space-y-4">
        <Field label={t('designer.editor.title_label')}>
          <input
            type="text"
            value={title}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_I18N_SECTION',
                sectionId: structural.section_id,
                locale: state.activeLocale,
                patch: { title: e.target.value },
              })
            }
            disabled={readOnly}
            className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
          />
        </Field>

        <Field
          label={t('designer.editor.section_id_label')}
          help={t('designer.editor.section_id_help_auto')}
        >
          <input
            type="text"
            value={structural.section_id}
            disabled
            className="w-full bg-surface-muted border border-line rounded-md px-3 py-2 text-xs font-mono text-ink-subtle disabled:opacity-100"
          />
        </Field>

        {/* Placeholder — sólo en hojas con un input único (no aplica a
            key_value_grid que tiene múltiples rows, ni a fixed_block
            que es texto fijo, ni a containers). */}
        {!isContainer &&
        blockType !== 'fixed_block' &&
        blockType !== 'key_value_grid' ? (
          <Field
            label={t('designer.editor.placeholder_label')}
            help={t('designer.editor.placeholder_help')}
          >
            <input
              type="text"
              value={i18nSection?.placeholder ?? ''}
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_I18N_SECTION',
                  sectionId: structural.section_id,
                  locale: state.activeLocale,
                  patch: { placeholder: e.target.value },
                })
              }
              disabled={readOnly}
              placeholder={t('designer.editor.placeholder_meta_placeholder')}
              className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
            />
          </Field>
        ) : null}

        {/* Texto de ayuda — aplica a todas las hojas (incluyendo K/V grid)
            como guía general arriba/debajo del campo. NO aplica a
            containers ni a fixed_block. */}
        {!isContainer && blockType !== 'fixed_block' ? (
          <Field
            label={t('designer.editor.help_text_label')}
            help={t('designer.editor.help_text_help')}
          >
            <textarea
              value={i18nSection?.help_text ?? ''}
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_I18N_SECTION',
                  sectionId: structural.section_id,
                  locale: state.activeLocale,
                  patch: { help_text: e.target.value },
                })
              }
              disabled={readOnly}
              rows={2}
              placeholder={t('designer.editor.help_text_meta_placeholder')}
              className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body focus:outline-none focus:border-brand resize-y disabled:opacity-60"
            />
          </Field>
        ) : null}

        {/* Configuración type-específica i18n: body del fixed_block.
            Este campo es i18n y vive aquí (junto al título) porque es
            contenido localizable que el template author escribe — no se
            llena en runtime, se queda como callout fijo del documento. */}
        {blockType === 'fixed_block' ? (
          <Field
            label={t('designer.editor.fixed_block_body_label', {
              locale: state.activeLocale,
            })}
            help={t('designer.editor.fixed_block_body_help')}
          >
            <textarea
              value={i18nSection?.body ?? ''}
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_I18N_SECTION',
                  sectionId: structural.section_id,
                  locale: state.activeLocale,
                  patch: { body: e.target.value },
                })
              }
              disabled={readOnly}
              rows={6}
              placeholder={t('designer.editor.fixed_block_body_placeholder')}
              className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body leading-relaxed focus:outline-none focus:border-brand resize-y disabled:opacity-60"
            />
          </Field>
        ) : null}

        {/* Asistencia IA — bloque v2 (Fase 7 Doc 24). Soporta
            DetailedAiInstructions completo: purpose + must_include +
            must_avoid + examples.good + examples.bad. El tono / audiencia
            / instrucciones generales viven en el banner global a nivel doc.
            NO aplica a fixed_block: el body se llena en el Designer (no
            hay IA que asista al template author durante el diseño). */}
        {blockType !== 'fixed_block' ? (
          <>
            <AiInstructionsBlock
              locale={state.activeLocale}
              value={i18nSection?.ai_instructions}
              onChange={(next) =>
                dispatch({
                  type: 'UPDATE_I18N_SECTION',
                  sectionId: structural.section_id,
                  locale: state.activeLocale,
                  patch: { ai_instructions: next },
                })
              }
              readOnly={readOnly}
            />

            <ContextSectionsBlock
              locale={state.activeLocale}
              currentRefs={
                structural.context_sections ?? []
              }
              availableRefs={{
                intra: buildIntraRefCandidates(state, structural),
                cross: crossRefCandidates,
              }}
              readOnly={readOnly}
              onAdd={(ref) => {
                const current =
                  structural.context_sections ?? []
                if (current.includes(ref)) return
                dispatch({
                  type: 'UPDATE_STRUCTURAL',
                  sectionId: structural.section_id,
                  patch: {
                    context_sections: [...current, ref],
                  },
                })
              }}
              onRemove={(ref) => {
                const current =
                  structural.context_sections ?? []
                const next = current.filter((r) => r !== ref)
                dispatch({
                  type: 'UPDATE_STRUCTURAL',
                  sectionId: structural.section_id,
                  patch: {
                    context_sections: next.length > 0 ? next : undefined,
                  },
                })
              }}
            />

            <TokensPanel state={state} />
          </>
        ) : null}
      </div>

      {/* Sección estructural — el `type` no se cambia aquí; vive como
          BlockTypeBadge informativo en el header. Cambiar el tipo
          mid-life rompe la semántica del nodo (los block types tienen
          shapes de value distintos): si el user quiere otro tipo, mejor
          borra y crea uno nuevo. */}
      <div className="mt-8 pt-5 border-t border-line space-y-4">
        {/* Predefined keys — sólo para key_value_grid. Si tiene valor,
            el runtime presenta los keys ya pre-poblados (read-only) y
            el usuario solo llena los valores. Si vacío, modo "free
            keys" (el usuario añade pares libremente). */}
        {blockType === 'key_value_grid' ? (
          <Field
            label={t('designer.editor.kv_predefined_keys_label')}
            help={t('designer.editor.kv_predefined_keys_help')}
          >
            <LinesTextarea
              lines={
                structural.predefined_keys ?? []
              }
              disabled={readOnly}
              rows={4}
              placeholder={t('designer.editor.kv_predefined_keys_placeholder')}
              onCommit={(lines) =>
                dispatch({
                  type: 'UPDATE_STRUCTURAL',
                  sectionId: structural.section_id,
                  patch: {
                    predefined_keys: lines.length > 0 ? lines : undefined,
                  },
                })
              }
            />
          </Field>
        ) : null}

        {/* Structured table — schema v2 (Fase 10 Doc 24). El
            `table_config` reemplaza al legacy `columns` + i18n
            `column_labels` con un único objeto que incluye labels +
            placeholders bilingües inline + enum_values bilingües. El
            `min_rows`/`max_rows` se editan vía `ValidationRulesBlock`
            (mismo bloque que el resto de reglas numéricas). */}
        {blockType === 'structured_table' ? (
          <TableConfigBlock
            locale={state.activeLocale}
            activeLocales={Object.keys(state.definition.i18n)}
            value={structural.table_config}
            readOnly={readOnly}
            onChange={(next) =>
              dispatch({
                type: 'UPDATE_STRUCTURAL',
                sectionId: structural.section_id,
                patch: {
                  table_config: next,
                },
              })
            }
          />
        ) : null}

        {/* Code block — lenguaje default: pre-selecciona el dropdown del
            editor en runtime (no es i18n — el lenguaje es universal). */}
        {blockType === 'code_block' ? (
          <Field
            label={t('designer.editor.code_default_language_label')}
            help={t('designer.editor.code_default_language_help')}
          >
            <select
              value={
                structural.default_language ?? ''
              }
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_STRUCTURAL',
                  sectionId: structural.section_id,
                  patch: {
                    default_language: e.target.value || undefined,
                  },
                })
              }
              disabled={readOnly}
              className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-mono disabled:opacity-60"
            >
              {CODE_LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>
                  {lang || t('designer.editor.code_default_language_none')}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {/* "Requerida" no aplica a fixed_block — el usuario no llena
            nada, así que el flag de required no tiene efecto. */}
        {blockType !== 'fixed_block' ? (
          <ToggleField
            label={t('designer.editor.required_label')}
            help={t('designer.editor.required_help')}
            checked={Boolean(structural.required)}
            disabled={readOnly}
            onChange={(checked) =>
              dispatch({
                type: 'UPDATE_STRUCTURAL',
                sectionId: structural.section_id,
                patch: { required: checked },
              })
            }
          />
        ) : null}

        {/* Variant del callout — aplica a fixed_block. Define el tono
            visual (icono + color del borde + bg) y semántico del
            callout (note, info, tip, warning, critical, success,
            reference, instruction). */}
        {blockType === 'fixed_block' ? (
          <Field
            label={t('designer.editor.fixed_block_variant_label')}
            help={t('designer.editor.fixed_block_variant_help')}
          >
            <select
              value={
                structural.variant ?? 'note'
              }
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_STRUCTURAL',
                  sectionId: structural.section_id,
                  patch: {
                    variant: CALLOUT_VARIANTS.find((v) => v === e.target.value),
                  },
                })
              }
              disabled={readOnly}
              className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm disabled:opacity-60"
            >
              <option value="note">
                {t('designer.editor.fixed_block_variant_note')}
              </option>
              <option value="info">
                {t('designer.editor.fixed_block_variant_info')}
              </option>
              <option value="tip">
                {t('designer.editor.fixed_block_variant_tip')}
              </option>
              <option value="warning">
                {t('designer.editor.fixed_block_variant_warning')}
              </option>
              <option value="critical">
                {t('designer.editor.fixed_block_variant_critical')}
              </option>
              <option value="success">
                {t('designer.editor.fixed_block_variant_success')}
              </option>
              <option value="reference">
                {t('designer.editor.fixed_block_variant_reference')}
              </option>
              <option value="instruction">
                {t('designer.editor.fixed_block_variant_instruction')}
              </option>
            </select>
          </Field>
        ) : null}

        {/* Repetible solo aplica a contenedores. Para hojas, no se
            muestra (los items repetidos viven naturalmente como una
            lista o tabla; un rich_text repetible no tiene semántica
            clara). */}
        {isContainer ? (
          <ToggleField
            label={t('designer.editor.repeatable_label')}
            help={t('designer.editor.repeatable_help')}
            checked={Boolean(structural.repeatable)}
            disabled={readOnly}
            onChange={(checked) => {
              // Doc 06 §3.3.1 — al marcar repeatable, si el container
              // no cumple la regla del discriminador, ofrecer al user
              // crearlo automáticamente vía dialog. Si lo desmarca,
              // no hay regla que validar.
              if (!checked) {
                dispatch({
                  type: 'UPDATE_STRUCTURAL',
                  sectionId: structural.section_id,
                  patch: { repeatable: false },
                })
                return
              }
              const children = structural.children ?? []
              const first = children[0]
              const needsDiscriminator =
                children.length === 0 ||
                !first ||
                first.type !== 'plain_text' ||
                first.required !== true
              if (!needsDiscriminator) {
                // Ya cumple §3.3.1 — solo marcar repeatable.
                dispatch({
                  type: 'UPDATE_STRUCTURAL',
                  sectionId: structural.section_id,
                  patch: { repeatable: true },
                })
                return
              }
              setRepeatableDialog({
                open: true,
                reason: children.length === 0 ? 'empty' : 'wrong_first',
              })
            }}
          />
        ) : null}
        {repeatableDialog?.open ? (
          <RepeatableDiscriminatorDialog
            open
            sectionTitle={title || structural.section_id}
            reason={repeatableDialog.reason}
            onConfirmAutoCreate={() => {
              dispatch({
                type: 'ENSURE_REPEATABLE_DISCRIMINATOR',
                sectionId: structural.section_id,
              })
              setRepeatableDialog(null)
            }}
            onConfirmManual={() => {
              dispatch({
                type: 'UPDATE_STRUCTURAL',
                sectionId: structural.section_id,
                patch: { repeatable: true },
              })
              setRepeatableDialog(null)
            }}
            onCancel={() => setRepeatableDialog(null)}
          />
        ) : null}

        {/* Hint + min/max items cuando el nodo es repetible */}
        {isContainer && structural.repeatable ? (
          <div className="rounded-md border border-brand/30 bg-brand-soft/30 p-3 space-y-3">
            <p className="text-xs text-ink leading-relaxed">
              {firstTextChildLabel
                ? t('designer.editor.repeatable_hint_with_label', {
                    label: firstTextChildLabel,
                  })
                : t('designer.editor.repeatable_hint_fallback', {
                    group: title || structural.section_id,
                  })}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <NumberRule
                label={t('designer.editor.min_items_label')}
                value={
                  typeof structural.min_items === 'number'
                    ? structural.min_items
                    : ''
                }
                disabled={readOnly}
                onChange={(raw) => {
                  const parsed = parsePositiveInt(raw)
                  dispatch({
                    type: 'UPDATE_STRUCTURAL',
                    sectionId: structural.section_id,
                    patch: {
                      min_items: parsed,
                    },
                  })
                }}
              />
              <NumberRule
                label={t('designer.editor.max_items_label')}
                value={
                  typeof structural.max_items === 'number'
                    ? structural.max_items
                    : ''
                }
                disabled={readOnly}
                onChange={(raw) => {
                  const parsed = parsePositiveInt(raw)
                  dispatch({
                    type: 'UPDATE_STRUCTURAL',
                    sectionId: structural.section_id,
                    patch: {
                      max_items: parsed,
                    },
                  })
                }}
              />
            </div>
          </div>
        ) : null}

        {/* "+ Hijo" cuando el nodo es contenedor */}
        {isContainer && !readOnly ? (
          <AddChildButton
            templateName={
              state.definition.i18n[state.activeLocale]?.name ?? ''
            }
            onAdd={(bt) =>
              dispatch({
                type: 'ADD_SECTION',
                blockType: bt,
                parentId: structural.section_id,
              })
            }
          />
        ) : null}

        <ValidationRulesBlock
          blockType={blockType}
          validations={structural.validation ?? []}
          readOnly={readOnly}
          onChange={(rules) =>
            dispatch({
              type: 'SET_VALIDATIONS',
              sectionId: structural.section_id,
              validations: rules,
            })
          }
        />
      </div>

      {/* Confirm remove */}
      <Dialog
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        title={t('designer.editor.remove_section_confirm_title')}
        footer={
          <>
            <button
              type="button"
              onClick={() => setRemoveOpen(false)}
              className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md"
            >
              {t('designer.editor.remove_section_confirm_cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                dispatch({
                  type: 'REMOVE_SECTION',
                  sectionId: structural.section_id,
                })
                setRemoveOpen(false)
              }}
              className="px-5 py-2 bg-critical hover:bg-critical/90 text-canvas text-sm font-medium rounded-md"
            >
              {t('designer.editor.remove_section_confirm_yes')}
            </button>
          </>
        }
      >
        <p className="font-body text-sm text-ink">
          {t('designer.editor.remove_section_confirm_body', {
            title: title || structural.section_id,
          })}
        </p>
      </Dialog>
    </div>
  )
}

// ─── Pieces ────────────────────────────────────────────────────────────────
function LocaleTabs({
  active,
  availableLocales,
  onChange,
}: {
  active: SupportedLocale
  availableLocales: SupportedLocale[]
  onChange: (loc: SupportedLocale) => void
}) {
  const { t } = useTranslation('templates')
  const allLocales = SUPPORTED_LOCALES
  return (
    <nav className="flex border-b border-line">
      {allLocales.map((loc) => {
        const isActive = active === loc
        const isAvailable = availableLocales.includes(loc)
        return (
          <button
            key={loc}
            type="button"
            onClick={() => isAvailable && onChange(loc)}
            disabled={!isAvailable}
            className={
              isActive
                ? 'px-4 py-2 text-xs font-mono uppercase border-b-2 border-brand text-brand'
                : isAvailable
                  ? 'px-4 py-2 text-xs font-mono uppercase border-b-2 border-transparent text-ink-muted hover:text-ink'
                  : 'px-4 py-2 text-xs font-mono uppercase border-b-2 border-transparent text-ink-subtle cursor-not-allowed opacity-50'
            }
          >
            {loc === 'es'
              ? t('designer.editor.tab_es')
              : t('designer.editor.tab_en')}
          </button>
        )
      })}
    </nav>
  )
}

function Field({
  label,
  help,
  children,
}: {
  label: string
  help?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-muted">{label}</span>
      <div className="mt-1">{children}</div>
      {help ? (
        <span className="mt-1 block text-[11px] text-ink-subtle">{help}</span>
      ) : null}
    </label>
  )
}

function ToggleField({
  label,
  help,
  checked,
  disabled,
  onChange,
}: {
  label: string
  help?: string
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="mt-1 accent-brand"
      />
      <div>
        <span className="text-sm text-ink">{label}</span>
        {help ? (
          <span className="block text-[11px] text-ink-subtle">{help}</span>
        ) : null}
      </div>
    </label>
  )
}

// ─── ValidationsEditor (legacy v1 — eliminado en Fase 9) ──────────────────
// Sustituido por `ValidationRulesBlock` que expone add/remove dinámico
// con dropdown filtrado por tipo + warnings min>max inline. La edición
// de `pattern`/`item_pattern` (regex) queda preservada en data via
// passthrough; cuando se necesite UI v2, agregar un sub-bloque
// dedicado. Ver bitácora §10 (Fase 9).

/** Textarea cuyo valor canónico es un `string[]` (una línea por item),
 * pero internamente mantiene un draft local mientras el usuario edita.
 *
 * El draft local vive solo mientras el user edita: se confirma vía
 * onCommit en blur y vuelve a mostrarse el valor canónico, así un cambio
 * externo (undo, reset) se ve sin resincronizar estado. Esto resuelve el
 * bug clásico de "filtrar líneas vacías en cada keystroke": al pulsar
 * Enter, el array se reduce, el join pierde el `\n`, el textarea se
 * re-renderiza sin el salto y el cursor brinca hacia atrás.
 *
 * El parse al persistir: `text.split('\n').map(trim).filter(Boolean)`.
 * Vacío total → array vacío. */
function LinesTextarea({
  lines,
  disabled,
  rows,
  placeholder,
  onCommit,
}: {
  lines: string[]
  disabled: boolean
  rows: number
  placeholder?: string
  onCommit: (lines: string[]) => void
}) {
  const canonicalText = lines.join('\n')
  // `null` mientras el user no esté editando.
  const [draft, setDraft] = useState<string | null>(null)

  function commit() {
    if (draft === null) return
    const parsed = draft
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
    if (parsed.join('\n') !== canonicalText) {
      onCommit(parsed)
    }
    setDraft(null)
  }

  return (
    <textarea
      value={draft ?? canonicalText}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      disabled={disabled}
      rows={rows}
      placeholder={placeholder}
      spellCheck={false}
      className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand resize-y disabled:opacity-60"
    />
  )
}

function NumberRule({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string
  value: number | ''
  disabled: boolean
  onChange: (raw: string) => void
}) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono text-ink-subtle">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={1}
        disabled={disabled}
        className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-1.5 text-sm font-mono"
      />
    </label>
  )
}

// ─── "+ Hijo" button — abre el modal picker ────────────────────────────────
function AddChildButton({
  templateName,
  onAdd,
}: {
  templateName: string
  onAdd: (bt: BlockType) => void
}) {
  const { t } = useTranslation('templates')
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-dashed border-line py-2 text-xs text-ink-muted hover:text-ink hover:border-line-strong transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
        {t('designer.editor.add_child')}
      </button>
      <AddSectionPickerDialog
        open={open}
        templateName={templateName}
        onClose={() => setOpen(false)}
        onPick={(bt) => onAdd(bt)}
      />
    </>
  )
}

// ─── buildIntraRefCandidates (Fase 8) ─────────────────────────────────────
/** Construye la lista de secciones intra eligibles para
 * `context_sections[]` de la sección activa:
 *   - Las anteriores en el documento que no la contienen
 *     (`backwardRefCandidates`, regla §3.3.2).
 *   - Excluye `fixed_block` (no persiste content útil para el agente).
 * Hidrata título bilingüe desde `definition.i18n.{es,en}.sections`.
 * El componente `ContextSectionsBlock` filtra adicionalmente las ya
 * referenciadas para el dropdown — esta función NO filtra eso porque
 * el mismo arreglo se usa también para hidratar el título de los chips. */
function buildIntraRefCandidates(
  state: DesignerState,
  current: ArtifactStructuralSection,
): AvailableIntraRef[] {
  const titleEs = new Map<string, string>()
  const titleEn = new Map<string, string>()
  for (const s of state.definition.i18n.es?.sections ?? []) {
    if (s.section_id && s.title) titleEs.set(s.section_id, s.title)
  }
  for (const s of state.definition.i18n.en?.sections ?? []) {
    if (s.section_id && s.title) titleEn.set(s.section_id, s.title)
  }
  return backwardRefCandidates(state.definition.sections, current.section_id)
    .filter((node) => (node.type as string) !== 'fixed_block')
    .map((node) => ({
      section_id: node.section_id,
      title: {
        es: titleEs.get(node.section_id) ?? node.section_id,
        en: titleEn.get(node.section_id) ?? node.section_id,
      },
      type: node.type as string,
    }))
}

// ─── Tokens panel (referencia, click-to-copy) ──────────────────────────────
/** Lista los tokens disponibles para usar en `ai_instructions.purpose`,
 * `tone_audience` o `ai_global_instructions` del documento. El wiring real
 * al motor de prompts es Fase D — hoy es solo referencia con copy. */
function TokensPanel({ state }: { state: DesignerState }) {
  const { t } = useTranslation('templates')

  // Tokens fijos (siempre disponibles).
  const globalTokens: Array<{ token: string; key: string }> = [
    { token: '{{project.name}}', key: 'token_project_name' },
    { token: '{{project.code}}', key: 'token_project_code' },
    { token: '{{project.content_language}}', key: 'token_project_language' },
    { token: '{{tenant.name}}', key: 'token_tenant_name' },
    { token: '{{document.name}}', key: 'token_document_name' },
    { token: '{{document.description}}', key: 'token_document_description' },
    { token: '{{document.tone_audience}}', key: 'token_document_tone' },
  ]

  // Tokens de secciones del árbol — todas las section_ids con `.value`.
  const sectionTokens: string[] = []
  walkTree(state.definition.sections, (n) => {
    sectionTokens.push(`{{${n.section_id}.value}}`)
  })

  // Tokens contextuales dentro de repetibles.
  const contextTokens: Array<{ token: string; key: string }> = [
    { token: '{{this.<field>}}', key: 'token_this_field' },
    { token: '{{this.parent.<field>}}', key: 'token_this_parent' },
  ]

  function copy(token: string) {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(token)
    }
  }

  return (
    <details className="rounded-md border border-line bg-surface-muted/40 p-3">
      <summary className="cursor-pointer text-xs font-medium text-ink-muted flex items-center gap-2">
        <Code2 className="w-3.5 h-3.5 text-brand" />
        {t('designer.editor.tokens_title')}
      </summary>
      <p className="mt-2 text-[11px] text-ink-subtle italic">
        {t('designer.editor.tokens_help')}
      </p>
      <div className="mt-3 space-y-3">
        <TokenGroup title={t('designer.editor.tokens_group_global')}>
          {globalTokens.map((tk) => (
            <TokenItem
              key={tk.token}
              token={tk.token}
              description={t(`designer.editor.${tk.key}`)}
              onCopy={() => copy(tk.token)}
            />
          ))}
        </TokenGroup>
        {sectionTokens.length > 0 ? (
          <TokenGroup title={t('designer.editor.tokens_group_sections')}>
            {sectionTokens.map((tk) => (
              <TokenItem
                key={tk}
                token={tk}
                description=""
                onCopy={() => copy(tk)}
              />
            ))}
          </TokenGroup>
        ) : null}
        <TokenGroup title={t('designer.editor.tokens_group_context')}>
          {contextTokens.map((tk) => (
            <TokenItem
              key={tk.token}
              token={tk.token}
              description={t(`designer.editor.${tk.key}`)}
              onCopy={() => copy(tk.token)}
            />
          ))}
        </TokenGroup>
      </div>
    </details>
  )
}

function TokenGroup({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle mb-1.5">
        {title}
      </p>
      <ul className="space-y-1">{children}</ul>
    </div>
  )
}

function TokenItem({
  token,
  description,
  onCopy,
}: {
  token: string
  description: string
  onCopy: () => void
}) {
  return (
    <li className="flex items-center gap-2">
      <button
        type="button"
        onClick={onCopy}
        title="Copiar al portapapeles"
        className="font-mono text-[11px] bg-canvas border border-line rounded px-1.5 py-0.5 text-brand hover:bg-brand-soft hover:border-brand/30 transition-colors"
      >
        {token}
      </button>
      {description ? (
        <span className="text-[11px] text-ink-subtle">{description}</span>
      ) : null}
    </li>
  )
}

